import { useState, useRef, useCallback } from 'react';
import { postChatMessage } from '../chatApi';
import { createSessionId, createWelcomeMessage, isWelcomeOnly } from '../messageUtils';

export function useChatSession() {
  const sessionIdRef = useRef(createSessionId());
  const [messages, setMessages] = useState([createWelcomeMessage()]);
  const [isLoading, setIsLoading] = useState(false);

  const showWelcome = isWelcomeOnly(messages, isLoading);

  const resetConversation = useCallback(({ onAfterReset } = {}) => {
    sessionIdRef.current = createSessionId();
    setMessages([createWelcomeMessage()]);
    setIsLoading(false);
    onAfterReset?.();
  }, []);

  const sendMessage = useCallback(async (rawText) => {
    const text = String(rawText || '').trim();
    if (!text || isLoading) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    const streamingId = `stream-${Date.now()}`;
    const streamingPlaceholder = {
      id: streamingId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      streaming: true
    };

    let conversationHistory = [];
    setMessages((prev) => {
      conversationHistory = prev
        .filter((entry) => entry.role === 'user')
        .map((entry) => entry.content)
        .slice(-5);
      return [...prev, userMessage, streamingPlaceholder];
    });

    setIsLoading(true);

    const reply = await postChatMessage({
      message: text,
      sessionId: sessionIdRef.current,
      conversationHistory,
      onChunk: (_chunk, fullText) => {
        setMessages((prev) =>
          prev.map((entry) =>
            entry.id === streamingId
              ? { ...entry, content: fullText, streaming: true }
              : entry
          )
        );
      }
    });

    setMessages((prev) => {
      const withoutPlaceholder = prev.filter((entry) => entry.id !== streamingId);
      return [
        ...withoutPlaceholder,
        {
          role: 'assistant',
          content: reply.content,
          timestamp: reply.timestamp,
          isError: reply.isError
        }
      ];
    });
    setIsLoading(false);
  }, [isLoading]);

  return {
    messages,
    isLoading,
    showWelcome,
    sendMessage,
    resetConversation
  };
}
